import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { AtiraEmpty } from "../../components/AtiraEmpty";
import { Flex } from "../../components/Flex";
import { SubHeader } from "../../components/SubHeader";
import { CampaignDrip } from "../../model/campaign/CampaignDrip";
import { campaignSliceSelectors } from "../../redux/campaign/campaign.selector";
import { campaignActions } from "../../redux/campaign/campaign.slice";
import { formSliceSelectors } from "../../redux/forms/form.selector";
import { formActions } from "../../redux/forms/form.slice";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { DripCampaign } from "./components/DripCampaign";
import { DripCampaignCreateEditDrawer } from "./components/DripCampaignCreateEditDrawer";
import { PageIcons } from "../../utils/GlobalIcons";
import { useDrawersContext } from "../tasks/drawers-context";
import { AtiraDropdown } from "../../components/AtiraDropdown";
import { Button } from "../../components/Button";
import { faBars } from "@fortawesome/free-solid-svg-icons/faBars";
import { AtiraIcon } from "../../components/AtiraIcon";
import { Text } from "../../components/Text";
import { MenuProps } from "../../utils/local-antd";

export const EmailCampaignsRoute: React.FC = () => {
  const [createEditCampaignDrawerVisible, setCreateEditCampaignDrawerVisible] =
    useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { setSubToUnlock } = useDrawersContext();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const isOrgMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);
  const forms = useAppSelector(formSliceSelectors.selectForms);
  const campaigns = useAppSelector(campaignSliceSelectors.selectCampaigns);

  const canCreateCampaign = !campaigns?.length;

  const onEdit = (campaign: CampaignDrip) => {
    dispatch(campaignActions.setSelectedCampaign(campaign));
    setCreateEditCampaignDrawerVisible(true);
  };

  const subHeaderActions: MenuProps["items"] = useMemo(() => {
    const onCreateCampaign = () => {
      if (!canCreateCampaign) {
        setSubToUnlock({
          visible: true,
          title: t("subToUnlock.specific.campaignsCount", { limit: 1 }),
        });
        return;
      }

      setCreateEditCampaignDrawerVisible(true);
    };

    return [
      {
        key: "campaign-create",
        label: (
          <Flex alignItems="center" gap="s">
            <AtiraIcon icon={faPlus} color="main" />
            <Text>{t("common.create")}</Text>
          </Flex>
        ),
        onClick: onCreateCampaign,
        hide: isOrgMember,
      },
    ].filter((action) => !action.hide);
  }, [canCreateCampaign, isOrgMember, setSubToUnlock, t]);

  useEffect(() => {
    dispatch(formActions.getForms({ userId }));
    dispatch(campaignActions.getCampaigns({ userId }));
  }, [dispatch, userId]);

  return (
    <Flex flexDirection="column">
      <SubHeader
        extra={
          subHeaderActions.length ? (
            <AtiraDropdown
              items={subHeaderActions}
              placement="bottomRight"
              trigger={["click"]}
              destroyOnHidden
            >
              <Button
                icon={faBars}
                color="black"
                padding="s"
                backgroundColor="white"
                hoverBackgroundColor="lightMain"
                hover
              >
                {t("common.options")}
              </Button>
            </AtiraDropdown>
          ) : null
        }
        title={t("subheader.campaigns")}
        icon={PageIcons.Campaigns}
      />

      {!campaigns.length && forms.length > 1 ? (
        <AtiraEmpty
          description={t("campaigns.dripCampaign.empty")}
          action={{
            title: t("campaigns.dripCampaign.empty.action"),
            onClick: () => setCreateEditCampaignDrawerVisible(true),
          }}
        />
      ) : null}

      {forms.length <= 1 ? (
        <AtiraEmpty
          description={t("campaigns.dripCampaign.noForm")}
          action={{
            title: t("campaigns.dripCampaign.noForm.action"),
            onClick: () => navigate("/forms"),
          }}
        />
      ) : null}

      <Flex flexDirection="column" gap="m" padding="m">
        {campaigns?.length
          ? campaigns.map((c) => (
              <DripCampaign key={c._id} campaign={c} onEdit={() => onEdit(c)} />
            ))
          : null}
      </Flex>

      <DripCampaignCreateEditDrawer
        open={createEditCampaignDrawerVisible}
        onClose={() => setCreateEditCampaignDrawerVisible(false)}
      />
    </Flex>
  );
};
